<template>
  <v-container class="pa-10" fluid>
    <v-row class="mb-7" justify="center">
      <h1>Trades per user</h1>
    </v-row>
    <v-form v-model="valid">
      <v-row align="center" justify="center">
          <v-col cols="6" sm="6" md="6" lg="3" xl="3">
            <v-autocomplete :items="this.$store.state.users" prepend-icon="fa-user"
                            label="User Email" item-text="user.email" item-value="user.id"
                            v-model="user" @change="userChanged" outlined/>
          </v-col>
          <v-col cols="6" sm="6" md="6" lg="3" xl="3">
            <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuFromDate">
              <template v-slot:activator="{ on }">
                <v-text-field :rules="fromDateRule" @blur="fromDate = parseDate(fromDateFormatted)"
                              label="From Date" :disabled="user === 0" prepend-icon="fa-calendar"
                              outlined persistent-hint readonly v-model="fromDateFormatted"
                              v-on="on"/>
              </template>
              <v-date-picker @change="valuesChanged" @input="menuFromDate = false" color="green lighten-1" v-model="fromDate"/>
            </v-menu>
          </v-col>
          <v-col cols="6" sm="6" md="6" lg="3" xl="3">
            <v-menu :close-on-content-click="false" offset-y transition="scale-transition" v-model="menuToDate">
              <template v-slot:activator="{ on }">
                <v-text-field :rules="toDateRule" @blur="toDate = parseDate(toDateFormatted)"
                              label="To Date" :disabled="user === 0" prepend-icon="fa-calendar"
                              outlined persistent-hint readonly v-model="toDateFormatted"
                              v-on="on"/>
              </template>
              <v-date-picker @change="valuesChanged" @input="menuToDate = false" color="green lighten-1" v-model="toDate"/>
            </v-menu>
          </v-col>
          <v-col cols="6" sm="6" md="6" lg="3" xl="3">
            <v-select :items="brokerAccounts" @change="valuesChanged" prepend-icon="fa-wallet"
                      item-value="id" label="Broker Account" :disabled="user === 0" outlined v-model="brokerSelected">
              <template v-slot:selection="data">
                {{ (data.item.broker.name !== 'All') ? ((data.item.identifier !== '') ? data.item.identifier : data.item.broker.name) : 'All' }}
                <v-icon v-if="data.item.verified" color="blue" class="ml-2" small>fa-check-circle</v-icon>
              </template>
              <template v-slot:item="data">
                {{ (data.item.identifier !== '' && data.item.identifier !== null) ? `${data.item.broker.name} · ${data.item.identifier}` : data.item.broker.name }}
                <v-icon v-if="data.item.verified" color="blue" class="ml-2" small>fa-check-circle</v-icon>
              </template>
            </v-select>
          </v-col>
      </v-row>
    </v-form>

    <!-- List of trades !-->
    <v-data-table :headers="headers" :items="trades">
      <template v-slot:item.id="{ item }">
        <i class="rowData">{{ item.id }}</i>
      </template>
      <template v-slot:item.side="{ item }">
        <i :style="`color: ${getTradeSideColor(item.side)}; font-size: 15px;`">{{ item.side }}</i>
      </template>
      <template v-slot:item.mood="{ item }">
        <v-chip :color="getMoodColor(item.mood)" dark small outlined>
          <v-icon left>{{ getMoodIcon(item.mood) }}</v-icon>
          {{ item.mood }}
        </v-chip>
      </template>
      <template v-slot:item.netProfit="{ item }">
        <v-chip :color="getNetProfitColor(item.netProfit)"><b style="color: #FFFFFF !important;">{{ item.netProfit }}</b></v-chip>
      </template>
      <template v-slot:item.strategy="{ item }">
        <i v-if="item.strategy != null" class="rowData">{{ item.strategy.name }}</i>
        <i v-else class="rowData">None</i>
      </template>
      <template v-slot:item.asset="{ item }">
        <i class="rowData">{{ item.asset }}</i>
      </template>
      <template v-slot:item.monetaryRisk="{ item }">
        <i class="rowData">{{ item.monetaryRisk.toFixed(2) }}</i>
      </template>
      <template v-slot:item.openTime="{ item }">
        <i class="rowData">{{ `${item.openTime.getDate()}/${item.openTime.getMonth() + 1}/${item.openTime.getFullYear()} ${String(item.openTime.getHours()).padStart(2, '0')}:${String(item.openTime.getMinutes()).padStart(2, '0')}:${String(item.openTime.getSeconds()).padStart(2, '0')}` }}</i>
      </template>
      <template v-slot:item.closeTime="{ item }">
        <i class="rowData">{{ `${item.closeTime.getDate()}/${item.closeTime.getMonth() + 1}/${item.closeTime.getFullYear()} ${String(item.closeTime.getHours()).padStart(2, '0')}:${String(item.closeTime.getMinutes()).padStart(2, '0')}:${String(item.closeTime.getSeconds()).padStart(2, '0')}` }}</i>
      </template>
      <template v-slot:item.tradeTime="{ item }">
        <i class="rowData">{{ getTradeTime(item.tradeTime) }}</i>
      </template>
      <template v-slot:item.entryPrice="{ item }">
        <i class="rowData">{{ item.entryPrice }}</i>
      </template>
      <template v-slot:item.actions="{ item }">
        <v-icon small class="icon mr-3" @click="showTrade(item)">fa-eye</v-icon>
        <v-icon small class="icon" @click="deleteTrade(item)">fa-trash</v-icon>
      </template>
    </v-data-table>

    <!-- Overlay to see full trade !-->
    <v-overlay v-model="overlayFullTrade">
      <FullTrade :trade="tradeToView" @closeWindow="overlayFullTrade = false"/>
    </v-overlay>
    <!-- Overlay to delete a trade !-->
    <v-overlay v-model="overlayDelete">
      <DeleteTrade :trade="tradeToView" @alreadyDeleted="deleteSpecificTrade" @closeWindow="overlayDelete = false"/>
    </v-overlay>
  </v-container>
</template>

<script>
import HelpDeskRepository from "../../repositories/helpDesk";
import Repository from '../../repositories/trades';
import FullTrade from "@/views/Trades/components/FullTrade";
import DeleteTrade from "@/views/Trades/components/DeleteTrade";
export default {
  name: "Trades",
  components: {DeleteTrade, FullTrade},
  props: {
    userId: Number,
  },
  data() {
    return {
      // Overlays
      tradeToView: null,
      overlayFullTrade: false,
      overlayDelete: false,

      // Filters
      user: 0,
      brokerAccounts: [{broker: {name: 'All'}, identifier: '', id: 'All'}],
      fromDate: new Date().toISOString().substr(0, 10),
      toDate: new Date().toISOString().substr(0, 10),
      fromDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
      toDateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
      menuFromDate: false,
      menuToDate: false,
      brokerSelected: { broker: { name: '' }, identifier: 'All',},

      trades: [],
      headers: [
        { text: 'Trade Id', align: 'left', value: 'id' },
        { text: 'Asset', align: 'left', value: 'asset.assetName' },
        { text: 'Side', value: 'side' },
        { text: 'Open Time', value: 'openTime' },
        { text: 'Close Time', value: 'closeTime' },
        { text: 'Trade Time', value: 'tradeTime' },
        { text: 'Entry Price', sortable: false, value: 'entryPrice' },
        { text: 'Strategy', value: 'strategy' },
        { text: 'Net Profit', value: 'netProfit' },
        { text: 'Monetary Risk', value: 'monetaryRisk'},
        { text: 'Actions', value: 'actions'},
      ],

      //Rules
      valid: false,
      fromDateRule: [
        // eslint-disable-next-line no-unused-vars
        f => this.fromDate <= this.toDate || 'From date must be before the last date',
      ],
      toDateRule: [
        // eslint-disable-next-line no-unused-vars
        f => this.fromDate <= this.toDate || 'From date must be before the last date',
      ],
    };
  },
  watch: {
    fromDate() {
      this.fromDateFormatted = this.formatDate(this.fromDate);
    },
    toDate() {
      this.toDateFormatted = this.formatDate(this.toDate);
    }
  },
  mounted() {
    this.specificUser();
    this.reset();
  },
  methods: {
      specificUser() {
        if(this.userId) {
          this.user = this.userId;
          this.userChanged();
        }
      },
      formatDate(date) {
        if (!date) return null;

        const [year, month, day] = date.split('-');
        return `${month}/${day}/${year}`
      },
      parseDate(date) {
        if (!date) return null;

        const [month, day, year] = date.split('/');
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
      },
      userChanged() {
        this.trades = [];
        this.brokerAccounts = [{broker: {name: 'All'}, identifier: '', id: 'All'}];
        HelpDeskRepository.allBrokerAccountsOfUser(this.user).then((response) => {
            if(response.status < 400)
              response.data.forEach((brokerAccount) => {
                this.brokerAccounts.push(brokerAccount);
              });
          });
      },
      reset() {
        let now = new Date();
        this.toDate = now.toISOString().substr(0, 10);
        this.toDateFormatted = this.formatDate(this.toDate);

        now.setTime((now.getTime() - 2.628e+9));
        this.fromDate = now.toISOString().substr(0, 10);
        this.fromDateFormatted = this.formatDate(this.fromDate);
      },
      valuesChanged() {
        if (this.valid) {
          let fromDataFiltered = new Date(this.fromDate.substr(0, 4), this.fromDate.substr(5, 2) - 1, this.fromDate.substr(8, 2), 0, 0);
          let toDateFiltered = new Date(this.toDate.substr(0, 4), this.toDate.substr(5, 2) - 1, this.toDate.substr(8, 2), 0, 0);
          toDateFiltered.setDate(toDateFiltered.getDate() + 1);
          let filter = {
            userId: this.user,
            side: 'All',
            mood: 'All',
            asset: 'All',
            result: 'All',
            strategy: 'All',
            fromDate: fromDataFiltered,
            toDate: toDateFiltered,
            brokerAccount: this.brokerSelected,
          };

          Repository.filteredTradesOfExternalUser(filter).then((response) => {
            if (response.status < 400) {
              let tradesCurated = response.data;
              tradesCurated.forEach((item) => {
                item.mood = item.mood[0] + item.mood.slice(1).toLowerCase();
                item.side = item.side[0] + item.side.slice(1).toLowerCase();
                item.tradeTime = this.convertDuration(item.tradeTime);
                item.openTime = new Date(item.openTime);
                item.closeTime = new Date(item.closeTime);
              });
              this.trades = tradesCurated;
            }
          });
        }
      },

      // Format related
      getTradeSideColor(side) {
        return (side.toUpperCase() === "BUY") ? '#008CFF' : '#FF7300';
      },
      getNetProfitColor(netProfit) {
        return (netProfit > 0) ? '#00a575' : '#ff6b55';
      },
      getMoodColor(mood) {
        switch (mood) {
          case 'Relaxed':
            return "#4d6556";
          case 'Cautious':
            return "#386538";
          case 'Concentrated':
            return "#376563";
          case 'Confident':
            return "#376563";
          case 'Excited':
            return "#28655c";
          case 'Anxious':
            return "#28655c";
          case 'Happy':
            return "#0f6500";
          case 'Optimistic':
            return "#1d5465";
          case 'Aggressive':
            return "#651540";
          case 'Angry':
            return "#650700";
          case 'Bored':
            return "#65412e";
          case 'Mad':
            return "#651540";
          case 'Nervous':
            return "#651540";
          case 'Sad':
            return "#65412e";
          case 'Upset':
            return "#651540";
          case 'None':
            return "#555555";
          default:
            return "#651540";
        }
      },
      getMoodIcon(mood) {
        switch (mood) {
          case 'Relaxed':
            return "fa-laugh-wink";
          case 'Cautious':
            return "fa-smile";
          case 'Concentrated':
            return "fa-meh-blank";
          case 'Confident':
            return "fa-smile-beam";
          case 'Excited':
            return "fa-grin-tongue";
          case 'Happy':
            return "fa-grin-beam";
          case 'Optimistic':
            return "fa-grin-stars";
          case 'Aggressive':
            return "fa-meh-rolling-eyes";
          case 'Angry':
            return "fa-angry";
          case 'Bored':
            return "fa-frown";
          case 'Mad':
            return "fa-angry";
          case 'Nervous':
            return "fa-grimace";
          case 'Sad':
            return "fa-sad-tear";
          case 'Upset':
            return "fa-frown-open";
          case 'None' :
            return "fa-ban";
          default:
            return "fa-flushed";
        }
      },
      convertDuration(isoFormat) {
        let iso8601DurationRegex = /(-)?P(?:([.,\d]+)Y)?(?:([.,\d]+)M)?(?:([.,\d]+)W)?(?:([.,\d]+)D)?T(?:([.,\d]+)H)?(?:([.,\d]+)M)?(?:([.,\d]+)S)?/;
        let matches = isoFormat.match(iso8601DurationRegex);

        // Sum to the trade time average
        this.tradeTimeAvg += (matches[8] === undefined ? 0 : matches[8] * 1) +
            (matches[7] === undefined ? 0 : matches[7] * 60) +
            (matches[6] === undefined ? 0 : matches[6] * 3600) +
            (matches[5] === undefined ? 0 : matches[5] * 86400) +
            (matches[4] === undefined ? 0 : matches[4] * 604800);

        return {
          sign: matches[1] === undefined ? '+' : '-',
          years: matches[2] === undefined ? 0 : matches[2],
          months: matches[3] === undefined ? 0 : matches[3],
          weeks: matches[4] === undefined ? 0 : matches[4],
          days: matches[5] === undefined ? 0 : matches[5],
          hours: matches[6] === undefined ? 0 : matches[6],
          minutes: matches[7] === undefined ? 0 : matches[7],
          seconds: matches[8] === undefined ? 0 : matches[8]
        };
      },
      getTradeTime(duration){
        let message = (duration.days > 0)? `${duration.days}d`: '';
        message = `${message} ${(duration.hours > 0)? `${duration.hours}h`: ''}`;
        message = `${message} ${(duration.minutes > 0)? `${duration.minutes}m`: ''}`;
        message = `${message} ${(duration.seconds > 0)? `${duration.seconds}s`: ''}`;
        return message;
      },
      showTrade(trade) {
        this.tradeToView = trade;
        this.overlayFullTrade = true;
      },
      deleteTrade(trade) {
        this.tradeToView = trade;
        this.overlayDelete = true;
      },
      deleteSpecificTrade() {
        //let index = this.trades.indexOf(this.tradeToView);
        //this.trades.splice(index, 1);
        this.valuesChanged();
        this.overlayDelete = false;
      }
    }
}
</script>

<style scoped>
  .rowData {
    font-size: 15px;
    color: #3b3f48;
  }
</style>
